// @ts-check
import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import { compose, find, isNil, map, prop } from 'ramda'
import { Button, CountdownTimer, Label, Error } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import { PhoneCodeVerification } from './PhoneCodeVerification'
import { usePrevious } from '../../utils/hooks'
import { sendVerificationCode } from '../../api'
import { getUserValue } from '../utils'
import styles from './style.css'

const MIN_PHONE_LENGTH = 8
const PHONE_MAX_LENGTH = 15

/**
 * @param {string} phoneCode
 * @param {string} phoneValue
 * @returns {boolean}
 */
const isPhoneValid = (phoneCode, phoneValue) => {
  const length = phoneCode.length + phoneValue.length
  return (phoneCode.length >= 1 && phoneCode.length <= 4) &&
   (length >= MIN_PHONE_LENGTH && length <= PHONE_MAX_LENGTH)
}

const getCountryCode = phoneCode =>
  compose(
    prop('phoneCode'),
    find(country => country.countryCode === phoneCode)
  )

/**
 @param {{
 label: string,
 name: string,
 phoneVerificationResendTime?: number,
 required?: boolean,
 showLabelAsPlaceholder?: boolean,
 order?: number
 }} props */
const ExternalPhone = ({
  label,
  name,
  phoneVerificationResendTime,
  required,
  showLabelAsPlaceholder
}) => {
  const { t } = useTranslation()

  const [
    {
      whitelabel: { mobileVerificationEnabled, defaultCountry },
      form,
      form: { accId },
      data: { countries },
      user
    },
    dispatch
  ] = useStore()
  const { citizenshipCountry, residenceCountry } = form[accId]
  const userValue = getUserValue(accId, user, name)
  const userCode = getUserValue(accId, user, 'phoneCode')
  const prevCountries = usePrevious(countries)

  const [statesManager, setStatesManager] = useState({
    phoneNumber: '',
    isCodeSent: false,
    isTimerShown: false,
    error: '',
    isCodeVerified: false,
    isSendButtonClicked: false,
    countryCode: '',
    isValid: false
  })

  const {
    countryCode,
    isCodeVerified,
    isCodeSent,
    isTimerShown,
    isSendButtonClicked,
    phoneNumber,
    error,
    isValid
  } = statesManager

  useEffect(() => {
    if (isCodeVerified || isValid || !countries.length) return
    const countryCode = getCountryCode(
      residenceCountry || citizenshipCountry
    )(countries) || ''
    setStatesManager(prevState => ({ ...prevState, countryCode }))
  }, [citizenshipCountry, residenceCountry, countries, isValid, isCodeVerified])

  useEffect(() => {
    const isCountriesReceivedFirstTime = (!prevCountries || !prevCountries.length) &&
      (countries && countries.length)
    if (!isCountriesReceivedFirstTime || countryCode.length || !defaultCountry) return
    const newCountryCode = getCountryCode(defaultCountry)(countries)
    setStatesManager(prevState => ({ ...prevState, countryCode: newCountryCode }))
  }, [countries, defaultCountry, countryCode])

  useEffect(() => {
    if (!isNil(userValue) && !isNil(userCode)) { // userCode exists only on frontend to preset value
      const userPhoneNumber = userValue.replace(userCode, '')
      setStatesManager(prevState => (
        { ...prevState, countryCode: userCode, phoneNumber: userPhoneNumber, isValid: true })
      )
      dispatch({ type: 'setExtFieldValue', name, value: userValue })
      dispatch({ type: 'setValidationFld', name, value: true })
      if (mobileVerificationEnabled) {
        setStatesManager(prevState => (
          { ...prevState, isCodeVerified: true })
        )
        dispatch({ type: 'setValidationFld', name: 'mobileVerification', value: true })
      }
    }
  }, [userValue, userCode, accId, mobileVerificationEnabled, name])

  const placeholder = (showLabelAsPlaceholder && t('generatedField.phoneNumber'))
  const isDisabled = isCodeSent && isTimerShown
  const isSentButtonDisabled = !isPhoneValid(countryCode, phoneNumber) || isSendButtonClicked

  /**
   * @param {boolean} isEnabled
   * @param {Function} callback
   */
  const handleKeyPress = (isEnabled, callback) =>
  /**
   * @param {{ charCode: number, target: Object }} e
   */
    ({
      charCode,
      target: { value, name }
    }) => {
      if (isEnabled && charCode === 13) {
        callback()
        checkIfValid(value, name)
      }
    }

  const sendCode = () => {
    setStatesManager(prevState => ({ ...prevState, isSendButtonClicked: true }))

    /**
     * @param {{status: string}} param
     */
    const resolve = ({ status }) => {
      if (status !== 'SEND_CODE_SUCCESSFUL') {
        setStatesManager(prevState => ({
          ...prevState,
          error: t('verifyPhone.codeCantBeSent')
        }))
      } else {
        setStatesManager(prevState => ({
          ...prevState,
          isTimerShown: true,
          isCodeSent: true
        }))
      }
    }

    const reject = () =>
      setStatesManager(prevState => ({ ...prevState, error: t('verifyPhone.codeCantBeSent') }))

    sendVerificationCode(countryCode, phoneNumber).then(resolve, reject)
  }
  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onChange = ({ target: { value, name } }) => {
    const code = name === 'countryCode' ? value : countryCode
    const codeLength = code.length || 1
    const maxLength = name === 'countryCode' ? 4 : (PHONE_MAX_LENGTH - codeLength)
    const reg = /^\d+$/
    const isValidInput = (value.length && reg.test(value)) || !value.length
    if (value.length <= maxLength && isValidInput) {
      setStatesManager(prevState => ({ ...prevState, [name]: value }))
    }
  }
  /**
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const onBlur = ({ currentTarget: { value, name: curFldName } }) => {
    checkIfValid(value, curFldName)
  }

  const checkIfValid = (value, curFldName) => {
    const code = curFldName === 'countryCode' ? value : countryCode
    const phone = curFldName === 'countryCode' ? phoneNumber : value
    const isValid = isPhoneValid(code, phone)
    setStatesManager(prevState => ({ ...prevState, isValid }))
    dispatch({ type: 'setExtFieldValue', name, value: `${code}${phone}` })
    dispatch({ type: 'setExtFieldValue', name: 'phoneCode', value: code })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  const onTimerEnd = () =>
    setStatesManager(prevState => ({
      ...prevState,
      isTimerShown: false,
      isSendButtonClicked: false
    }))
  /**
   * @param {boolean} isEnterEnabled
   */
  const maskedInput = (isEnterEnabled) => (
    <>
      <div className={styles.field}>
        {!showLabelAsPlaceholder && (
          <Label htmlFor={t(label)} value={t(label)} valid={isValid} />
        )}
        <div className={styles.flexBlock} dir='ltr'>
          <span className={styles.plus}> + </span>
          <input
            className={cn(styles.input, styles.code)}
            name='countryCode'
            type='text'
            placeholder={placeholder}
            value={countryCode}
            onChange={onChange}
            onBlur={onBlur}
            autoComplete='nope'
            disabled={isDisabled || isCodeVerified}
          />
          <input
            className={styles.input}
            id={t(label)}
            name={name}
            type='text'
            placeholder={placeholder}
            onKeyPress={handleKeyPress(isEnterEnabled, sendCode)}
            value={phoneNumber}
            onChange={onChange}
            onBlur={onBlur}
            autoComplete='nope'
            disabled={isDisabled || isCodeVerified}
          />
        </div>
        <Error value={error} />
      </div>
    </>
  )

  const setIsCodeVerified = () => {
    setStatesManager(prevState => ({ ...prevState, isCodeVerified: true }))
  }

  if (mobileVerificationEnabled) {
    return (
      <div className={styles.phoneBlock}>
        {maskedInput(mobileVerificationEnabled && !isSentButtonDisabled)}
        {!isCodeVerified && (
          <Button disabled={isSentButtonDisabled} onClick={sendCode}>
            {t('verifyPhone.sendCode')}
          </Button>
        )}
        <div className={styles.error}>{error}</div>
        <div className={cn(styles.time, 'notranslate')}>
          {isDisabled && !isCodeVerified && (
            <CountdownTimer
              seconds={phoneVerificationResendTime}
              onEnd={onTimerEnd}
            />
          )}
        </div>
        {isCodeSent && !isCodeVerified && (
          <PhoneCodeVerification
            setIsCodeVerified={setIsCodeVerified}
          />
        )}
        {isCodeVerified && <div>{t('verifyPhone.phoneVerified')}</div>}
      </div>
    )
  } else {
    return maskedInput(mobileVerificationEnabled)
  }
}

const getExternalPhoneFields = map(phoneFields => {
  const { i18n, name, required, phoneVerificationResendTime, order } = phoneFields
  return (
    <ExternalPhone
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      key={name}
      phoneVerificationResendTime={phoneVerificationResendTime}
      order={order}
    />
  )
})

export { ExternalPhone, getExternalPhoneFields }
